import React, { useState, useMemo, useEffect } from "react";
import { Container, Card, CardBody, Spinner, Row, Col, Button } from "reactstrap";
import { useForm } from "react-hook-form";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import styles from "../css/auditlog.module.scss";
import CommonButton from "Components/Common/CommonButton";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import GenerateUserActivityReportModal from "../components/GenerateUserActivityReportModal";
import { useAuditService } from "../api/useAuditQuery";
import { parseLocalDate } from "helpers/constant";
import {
  getUserActivityFields,
  getUserActivityColumns,
} from "../helper/auditconstant";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "common/constants/PermissionConstants";
import { showToastMessage } from "common/service/CommonService";
import {
  LoginActivityRecord,
  UserActivityFilterFormValues,
} from "../helper/audit.types";

const UserActivity: React.FC = () => {
  document.title = "User Activity | ZENTIXS VISTA";

  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);

  const pageSize = 10;

  const {
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<UserActivityFilterFormValues>({
    defaultValues: {
      fromdate: null,
      todate: null,
      useractivity: "All",
    },
  });

  const fromDateValue = watch("fromdate");
  const toDateValue = watch("todate");
  const activityValue = watch("useractivity");

  useEffect(() => {
    if (fromDateValue && toDateValue) {
      const fromDate = parseLocalDate(fromDateValue);
      const toDate = parseLocalDate(toDateValue);
      if (fromDate && toDate && fromDate > toDate) {
        setValue("todate", null);
      }
    }
  }, [fromDateValue, toDateValue, setValue]);

  useEffect(() => {
    setCurrentPageNumber(1);
  }, [fromDateValue, toDateValue, activityValue]);

  const userActivityFields: FormFieldConfig[] = useMemo(
    () => getUserActivityFields(fromDateValue),
    [fromDateValue],
  );

  const { LoginSelectAllQuery, SelectAllMutation: GenerateReportMutation } =
    useAuditService(false, {
      currentpage: currentPageNumber,
      pagesize: pageSize,
      ispagination: true,
      fromdate: fromDateValue || null,
      todate: toDateValue || null,
      useractivity: activityValue === "All" ? null : activityValue || null,
    },
    true);

  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = LoginSelectAllQuery as {
    data?: { data?: LoginActivityRecord[] } | LoginActivityRecord[];
    isLoading: boolean;
    isFetching: boolean;
  };

  // Extract raw list from react-query response
  const rawList: LoginActivityRecord[] = useMemo(() => {
    if (!queryResponse) return [];
    if (Array.isArray(queryResponse)) return queryResponse;
    if (Array.isArray(queryResponse?.data)) return queryResponse.data;
    return [];
  }, [queryResponse]);

  const handleGenerateReport = (
    modalValues: UserActivityFilterFormValues,
  ) => {
    const payload = {
      modulename: "Login & Logout",
      fromdate: modalValues.fromdate || null,
      todate: modalValues.todate || null,
      useractivity: modalValues.useractivity === "All" ? null : modalValues.useractivity || null,
    };

    GenerateReportMutation.mutate(payload, {
      onSuccess: (data: any) => {
        setIsReportModalOpen(false);
        if (data instanceof Blob) {
          const pdfFile = new File([data], "User_Activity_Report.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        } else {
          showToastMessage("error", "Failed to generate report.");
        }
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message || "Error generating report");
        console.error("Error generating report:", error);
      },
    });
  };

  const columns = useMemo(
    () => getUserActivityColumns(currentPageNumber),
    [currentPageNumber],
  );

  return (
    <div className={styles.loginPageWrapper}>
      <Container fluid>
        {/* Header Section */}
        <div className="d-flex justify-content-between align-items-center px-2">
          <div>
            <h5 className={`mb-1 ${styles.pageHeaderTitle}`}>User Activity</h5>
            <p className={styles.pageHeaderSubtitle}>
              Track and manage system user login and logout activity records.
            </p>
          </div>
          <div className="d-flex gap-3 align-items-center">
            <CommonButton
              color="primary"
              outline
              className="px-3 fw-600"
              size="sm"
               onClick={() => setIsReportModalOpen(true)}
              disabled={GenerateReportMutation.isPending}
            >
                <i className="ri-file-pdf-line me-1 text-danger" />
              Generate Report
            </CommonButton>
          </div>
        </div>

        {/* List Panel with CommonTableIndexWise */}
        <Card>
          <CardBody className="pb-0">
            <div className="p-2">
              {/* CommonForm filter section */}
              <div className="mb-3 p-3 bg-light rounded border">
                <div className="d-flex align-items-end gap-3">
                  <div className="flex-grow-1">
                    <CommonForm
                      fields={userActivityFields}
                      control={control}
                      errors={errors}
                      columns={3}
                      setValue={setValue}
                      getValues={getValues}
                    />
                  </div>
                  <div className="mb-2">
                    <Button
                      outline
                      color="primary"
                      className={styles.iconBtn}
                      onClick={() =>
                        reset({
                          fromdate: null,
                          todate: null,
                          useractivity: "All",
                        })
                      }
                      title="Reset"
                    >
                      <i className="ri-refresh-line" />
                    </Button>
                  </div>
                </div>
              </div>

              <CommonTableIndexWise
                columns={columns}
                data={rawList}
                loading={isLoading || isFetching}
                pageSize={10}
                currentPage={currentPageNumber}
                totalRecords={rawList[0]?.totalrecords}
                onPageChange={setCurrentPageNumber}
              />
            </div>
          </CardBody>
        </Card>
      </Container>

      {/* Generate Report Selection Modal */}
      {isReportModalOpen && (
        <GenerateUserActivityReportModal
          isOpen={isReportModalOpen}
          toggle={() => setIsReportModalOpen(false)}
          onSubmit={handleGenerateReport}
          isGenerating={GenerateReportMutation.isPending}
        />
      )}

      {/* File Preview Modal for Generated Report */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size="xl"
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </div>
  );
};

export default UserActivity;
